import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownTrayIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { ExportJob } from '../services/genericExportService';
import { formatFileSize } from '../services/genericExportService';

export type ClientExportPhase = 'starting' | 'processing' | 'completed' | 'failed' | 'expired';

type ServerExportProgressModalProps = {
  variant: 'server';
  open: boolean;
  zClassName?: string;
  job: ExportJob | null;
  error: string | null;
  downloadButtonLabel?: string;
  onDownload: () => void;
  onClose: () => void;
};

type ClientExportProgressModalProps = {
  variant: 'client';
  open: boolean;
  zClassName?: string;
  phase: ClientExportPhase;
  errorMessage: string | null;
  clientSummaryLines?: string[];
  downloadButtonLabel?: string;
  onDownload?: () => void;
  onClose: () => void;
};

export type ExportProgressModalProps = ServerExportProgressModalProps | ClientExportProgressModalProps;

function renderServerContent(
  job: ExportJob | null,
  error: string | null,
  downloadButtonLabel: string,
  onDownload: () => void
): React.ReactNode {
  if (error) {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-red-500" />
        <p className="font-medium text-red-600">Export Failed</p>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center">
        <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        <p className="text-gray-600">Starting export...</p>
      </div>
    );
  }

  if (job.status === 'PENDING') {
    return (
      <div className="text-center">
        <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        <p className="text-gray-600">Queued for processing...</p>
      </div>
    );
  }

  if (job.status === 'PROCESSING') {
    return (
      <div className="text-center">
        <div className="relative mx-auto mb-3 w-48">
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${job.progress}%` }}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">{job.progress}%</p>
        </div>
        <p className="text-gray-600">
          Processing {job.processedRows?.toLocaleString() || 0} of {job.totalRows?.toLocaleString() || '?'} rows...
        </p>
        <p className="mt-2 text-xs text-gray-400">This may take a few minutes for large exports</p>
      </div>
    );
  }

  if (job.status === 'COMPLETED') {
    return (
      <div className="text-center">
        <CheckCircleIcon className="mx-auto mb-3 h-12 w-12 text-green-500" />
        <p className="font-medium text-green-600">Export Complete!</p>
        <p className="mt-1 text-sm text-gray-500">
          {job.totalRows?.toLocaleString()} rows exported
          {job.fileSize ? ` (${formatFileSize(job.fileSize)})` : ''}
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="mt-4 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
        >
          <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
          {downloadButtonLabel}
        </button>
        {job.expiresAt ? (
          <p className="mt-3 text-xs text-gray-400">Download link expires: {new Date(job.expiresAt).toLocaleString()}</p>
        ) : null}
      </div>
    );
  }

  if (job.status === 'FAILED') {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-red-500" />
        <p className="font-medium text-red-600">Export Failed</p>
        <p className="mt-1 text-sm text-gray-500">{job.errorMessage || 'Unknown error'}</p>
      </div>
    );
  }

  if (job.status === 'EXPIRED') {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-yellow-500" />
        <p className="font-medium text-yellow-600">Download Expired</p>
        <p className="mt-1 text-sm text-gray-500">Please start a new export</p>
      </div>
    );
  }

  return null;
}

function renderClientContent(
  phase: ClientExportPhase,
  errorMessage: string | null,
  clientSummaryLines: string[] | undefined,
  downloadButtonLabel: string,
  onDownload: (() => void) | undefined
): React.ReactNode {
  if (errorMessage) {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-red-500" />
        <p className="font-medium text-red-600">Export Failed</p>
        <p className="mt-1 text-sm text-gray-500">{errorMessage}</p>
      </div>
    );
  }

  if (phase === 'starting' || phase === 'processing') {
    return (
      <div className="text-center">
        <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        <p className="text-gray-600">{phase === 'starting' ? 'Starting export...' : 'Preparing your file...'}</p>
        <p className="mt-2 text-xs text-gray-400">This may take a moment for large exports</p>
      </div>
    );
  }

  if (phase === 'completed') {
    return (
      <div className="text-center">
        <CheckCircleIcon className="mx-auto mb-3 h-12 w-12 text-green-500" />
        <p className="font-medium text-green-600">Export Complete!</p>
        {clientSummaryLines?.map((line) => (
          <p key={line} className="mt-1 text-sm text-gray-500">
            {line}
          </p>
        ))}
        {onDownload ? (
          <button
            type="button"
            onClick={onDownload}
            className="mt-4 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
          >
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            {downloadButtonLabel}
          </button>
        ) : null}
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-red-500" />
        <p className="font-medium text-red-600">Export Failed</p>
        <p className="mt-1 text-sm text-gray-500">{errorMessage || 'Unknown error'}</p>
      </div>
    );
  }

  if (phase === 'expired') {
    return (
      <div className="text-center">
        <XCircleIcon className="mx-auto mb-3 h-12 w-12 text-yellow-500" />
        <p className="font-medium text-yellow-600">Download Expired</p>
        <p className="mt-1 text-sm text-gray-500">Please start a new export</p>
      </div>
    );
  }

  return null;
}

const ExportProgressModal: React.FC<ExportProgressModalProps> = (props) => {
  if (!props.open) return null;

  /** Above app shell (sidebar/header ~z-30), dashboard filter bars (z-50), and nested dialogs (e.g. z-[101]). */
  const zClassName = props.zClassName ?? 'z-[200]';

  const body =
    props.variant === 'server'
      ? renderServerContent(props.job, props.error, props.downloadButtonLabel ?? 'Download CSV', props.onDownload)
      : renderClientContent(
          props.phase,
          props.errorMessage,
          props.clientSummaryLines,
          props.downloadButtonLabel ?? 'Download CSV',
          props.onDownload
        );

  const canBackdropCloseServer =
    props.variant === 'server' &&
    (props.error ||
      props.job?.status === 'COMPLETED' ||
      props.job?.status === 'FAILED' ||
      props.job?.status === 'EXPIRED');

  const canBackdropCloseClient =
    props.variant === 'client' &&
    (props.phase === 'completed' || props.phase === 'failed' || props.phase === 'expired' || Boolean(props.errorMessage));

  const showFooterCloseServer =
    props.variant === 'server' &&
    (props.error ||
      props.job?.status === 'COMPLETED' ||
      props.job?.status === 'FAILED' ||
      props.job?.status === 'EXPIRED');

  const showFooterCloseClient =
    props.variant === 'client' &&
    (props.phase === 'completed' || props.phase === 'failed' || props.phase === 'expired' || Boolean(props.errorMessage));

  const modal = (
    <div className={`fixed inset-0 ${zClassName} overflow-y-auto`}>
      <div className="flex min-h-screen items-center justify-center px-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          role="presentation"
          onClick={canBackdropCloseServer || canBackdropCloseClient ? props.onClose : undefined}
        />

        <div className="relative z-10 w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
          <h3 className="mb-4 text-center text-lg font-medium text-gray-900">Export Progress</h3>

          {body}

          {(showFooterCloseServer || showFooterCloseClient) && (
            <button
              type="button"
              onClick={props.onClose}
              className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modal, document.body);
};

export default ExportProgressModal;
